"use client";

import {JSX, useState} from "react";

import {type KeenSliderInstance, useKeenSlider} from "keen-slider/react";

import {Amenity} from "@/entities/rental";

import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";
import {assetPath} from "@/shared/utils/asset-path";

import "./styles/sleeping-arrangements.scss";
import "keen-slider/keen-slider.min.css";

export interface BedroomInfo {
    id: string | number;
    name: string;
    sleeps: number;
    beds: Array<{
        type: string;
        count: number;
    }>;
}

export interface SleepingArrangementsProps {
    title?: string;
    bedrooms: Array<BedroomInfo>;
    className?: string;
}

export function SleepingArrangements({title, bedrooms, className = ""}: SleepingArrangementsProps): JSX.Element {
    const {t} = useTranslation("rental");
    const [currentSlide, setCurrentSlide] = useState(0);
    /** Snap positions (maxIdx + 1), not bedroom count — avoids extra dots when perView shows several cards */
    const [dotCount, setDotCount] = useState(0);

    const syncFromSlider = (s: KeenSliderInstance): void => {
        const details = s.track.details;
        if (!details) return;
        setCurrentSlide(details.rel);
        setDotCount(details.maxIdx + 1);
    };

    const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
        rubberband: true,
        slides: {perView: 1.4, spacing: 20},
        breakpoints: {
            "(min-width: 480px)": {slides: {perView: 2.4, spacing: 16}},
            "(min-width: 768px)": {slides: {perView: 3.5, spacing: 20}},
            "(min-width: 1024px)": {slides: {perView: 3.5, spacing: 20}},
        },
        created(s) {
            syncFromSlider(s);
            /**
             * Horizontal trackpad/wheel: block browser history navigation on macOS.
             * Do not use track.add(raw deltaX) — Keen expects drag-scaled deltas; raw pixels throw the track far off-screen.
             */
            let wheelAccumX = 0;
            const stepThresholdPx = 72;

            const wheelDeltaXPx = (e: WheelEvent): number => {
                let dx = e.deltaX;
                if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) {
                    dx *= 16;
                } else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) {
                    dx *= s.container.clientWidth || 1;
                }
                return dx;
            };

            const onWheel = (e: WheelEvent): void => {
                if (e.ctrlKey) return;
                if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
                e.preventDefault();

                wheelAccumX += wheelDeltaXPx(e);
                while (wheelAccumX >= stepThresholdPx) {
                    s.prev();
                    wheelAccumX -= stepThresholdPx;
                }
                while (wheelAccumX <= -stepThresholdPx) {
                    s.next();
                    wheelAccumX += stepThresholdPx;
                }
            };
            s.container.addEventListener("wheel", onWheel, {passive: false});
            s.on("destroyed", () => {
                s.container.removeEventListener("wheel", onWheel);
            });
        },
        slideChanged: syncFromSlider,
        updated: syncFromSlider,
    });

    const formatBeds = (bed: {type: string; count: number}): string => {
        // Use original bed type without translation (twin, double, queen, king, etc.)
        return t("bedWithType", {count: bed.count, type: bed.type});
    };

    return (
        <section className={`sleeping-arrangements ${className}`}>
            <Typography as="h2" size={{desktop: 24, mobile: 20}} weight={600} className="sleeping-arrangements__title">
                {title ?? t("sleepingArrangements")}
            </Typography>

            <div className="sleeping-arrangements__slider">
                <div ref={sliderRef} className="sleeping-arrangements__swiper keen-slider">
                    {bedrooms.map((bedroom) => (
                        <div key={bedroom.id} className="keen-slider__slide">
                            <div className="sleeping-arrangements__card">
                                <div className="sleeping-arrangements__card-header">
                                    <Typography as="h3" size={16} weight={600} className="sleeping-arrangements__card-title">
                                        {bedroom.name}
                                    </Typography>
                                    <Typography as="p" size={12} weight={400} color="paragraph2" className="sleeping-arrangements__card-sleeps">
                                        {t("sleeps", {count: bedroom.sleeps})}
                                    </Typography>
                                </div>

                                <div className="sleeping-arrangements__card-content">
                                    {bedroom.beds.map((bed, index) => (
                                        <Amenity
                                            key={index}
                                            icon={assetPath("/images/icons/property/bed.svg")}
                                            alt={t("beds")}
                                            text={formatBeds(bed)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {dotCount > 1 && (
                    <div className="sleeping-arrangements__dots">
                        {Array.from({length: dotCount}, (_, idx) => (
                            <button
                                key={idx}
                                type="button"
                                aria-label={t("goToSlide", {number: idx + 1})}
                                aria-current={currentSlide === idx ? "true" : undefined}
                                className={`sleeping-arrangements__dot${currentSlide === idx ? " sleeping-arrangements__dot--active" : ""}`}
                                onClick={() => slider.current?.moveToIdx(idx)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}
